import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";

/* ──────────────────────────────────────────────────────────────
 * 📦 getProjectList (인증 필요)
 *  - 401이면 authFetch가 refresh 후 1회 재시도
 * ────────────────────────────────────────────────────────────── */
function getProjectYear(createdAt) {
  if (!createdAt) return null;
  if (createdAt instanceof Date) return createdAt.getFullYear();

  const value = String(createdAt);
  const match = value.match(/^(\d{4})[-/]/);
  if (match) return Number(match[1]);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
}

function buildYearOptions(projectYears = []) {
  const currentYear = new Date().getFullYear();
  const safeYears = projectYears
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year) && year <= currentYear);
  const startYear = safeYears.length ? Math.min(...safeYears) : currentYear;

  return Array.from(
    { length: currentYear - startYear + 1 },
    (_, index) => String(startYear + index),
  );
}

export async function getProjectList(options = {}) {
  try {
    const { year, keyword, projectScope, sortDir } =
      options && typeof options === "object" ? options : {};
    const qs = new URLSearchParams();
    if (year) qs.set("year", String(year));
    if (keyword) qs.set("keyword", String(keyword));
    if (projectScope) qs.set("projectScope", String(projectScope));
    if (sortDir) qs.set("sortDir", String(sortDir));

    const queryString = qs.toString();
    const path = `${BACKEND_URL}/project/get_projectList${
      queryString ? `?${queryString}` : ""
    }`;

    // ✅ 여기서 fetch 대신 authFetch 사용
    const res = await authFetch(
      path,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      {
        // ✅ 너네 refresh 엔드포인트 경로에 맞춰서 조정
        // 예: `${BACKURL}auth/refresh` 라면 "auth/refresh" 만 포함돼도 OK
        refreshUrlIncludes: "auth/refresh",
      },
    );

    // ───────────────────────────────────────────────
    // STEP 2 : 응답 상태 체크 및 JSON 파싱
    // ───────────────────────────────────────────────
    if (!res.ok) {
      if (res.status === 401) {
        // refresh도 실패했거나 세션이 완전히 만료된 상태
        return {
          success: false,
          message: "인증이 만료되었습니다. 다시 로그인해 주세요.",
        };
      }
      console.error("❌ getProjectList: 서버 응답 실패", res.status);
      return { success: false, message: "프로젝트 목록 조회 실패" };
    }

    const payload = await res.json();
    const data = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.data?.projects)
        ? payload.data.projects
        : [];

    // ───────────────────────────────────────────────
    // STEP 3 : 최종 반환
    // ───────────────────────────────────────────────
    const serverYears = Array.isArray(payload?.yearList)
      ? payload.yearList
      : Array.isArray(payload?.data?.yearList)
        ? payload.data.yearList
        : data.map((project) => getProjectYear(project.created_at));
    const yearList = buildYearOptions(serverYears);

    return { success: true, projects: data, yearList };
  } catch (err) {
    console.error("❌ getProjectList 요청 실패:", err);
    return { success: false, message: "서버 요청 중 오류가 발생했습니다." };
  }
}
